import React, { useState, useEffect } from 'react';
import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import { Badge, Button, Icon, Input, Search, Select, Tooltip } from '@scuf/common';
import i18next from '../../../i18next';
import useFetch, { IUseFetch } from '../../commonUtils/useFetch';
import { IOrganisation, IOrganisationResponse, IPermission } from '../onboardingModels';
import './ManageSites-theme.scss';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '../onboardingUtils';
import { ReactComponent as UnLinkIcon } from '../../../assets/UnLink.svg';
import useDebounce from '../../commonUtils/useDebounce';
import { AlertModal } from './AlertModal';
import toasts from '../../commonUtils/toasts';
import { IFeature, ISearchSiteList, ISearchSiteListGroup, ISiteResponse, ISubscriptionList } from './SKULIst/SKUModels';
import { useAuthService } from '../../../auth/hooks/useAuthService';

interface IManageSitesProps extends IAPIPackage, ISubscriptionList {
  handleChange: (event: any, type: string) => void;
  onAddClick: (addClick: boolean) => void;
  isAddOn: boolean;
  associatedResources: IFeature[]
}

export const ManageSites = (props: IManageSitesProps) => {
  const { baseURL, hasPermission } = useAuthService();
  const [organisationSelected, setOrganisationSelected] = useState<
    IOrganisation
  >();
  const [siteSelected, setSiteSelected] = useState<ISearchSiteList>();
  const [isSiteSelected, setIsSiteSelected] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [numberOfAddon, setNumberOfAddon] = useState<string>('1');
  const debouncedValue = useDebounce<string>(value, 500);
  const [displaySitesection, setDisplaySiteSection] = useState<boolean>(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);
  const [selectedSiteForUnlink, setSelectedSiteForUnlink] = useState<
    ISearchSiteList
  >();
  const [associatedSites, setAssociatedSites] = useState<ISearchSiteList[]>(
    props.associatedResources
  );
  const [duplicateSiteError, setDuplicateSiteError] = useState<boolean>(false);
  const [hasTargetSitePermission, setHasTargetSitePermission] = useState<boolean>(false);
  const [hasAuthorizedError, setHasAuthorizedError] = useState<boolean>(false);

  const groupBy = (arr: any[], key: string) => {
    const initialValue = {};
    return arr.reduce((acc, cval) => {
      const myAttribute = cval[key];
      acc[myAttribute] = [...(acc[myAttribute] || []), cval]
      return acc;
    }, initialValue);
  };

  const associatedSitesGroupBy: ISearchSiteListGroup[] = React.useMemo(() => {
    const groupByName: {[key: string]: ISearchSiteList[]}  = groupBy(associatedSites, 'id');
    return Object.keys(groupByName).map(item => {
      const isNew = groupByName[item].filter((siteList: ISearchSiteList) => siteList.isNew).length? true: false
      return {
        name: groupByName[item][0].name,
        count: groupByName[item].length,
        title: groupByName[item][0].title,
        orgId: groupByName[item][0].orgId,
        siteId: groupByName[item][0].siteId,
        desc: groupByName[item][0].desc,
        id: groupByName[item][0].id,
        address: groupByName[item][0].address,
        isNew: isNew
      };
    });
  }, [associatedSites]);
  const [
    organisationData,
    isLoadingOrganisationData,
    errorOrganisationData,
    fetchOrganisationData
  ]: IUseFetch<IOrganisationResponse> = useFetch(
    `${props.baseURL}/api/RootOrganisations/${props.offering}`,
    props.authFetch,
    undefined,
    true
  );

  const [
    permissions,
    isLoadingPermissions,
    errorPermissions,
    fetchPermissions
  ]: IUseFetch<IPermission[]> = useFetch(
    `${baseURL}/api/v1/users/orgs/${organisationSelected ? organisationSelected.id : undefined}/nodes/${organisationSelected ? organisationSelected.id : undefined}/privileges?scopes=SubscriptionManagement`,
    props.authFetch,
    undefined,
    true
  );
  
  const [
    siteList,
    isLoadingSiteList,
    errorSiteList,
    fetchSiteList
  ]: IUseFetch<ISiteResponse> = useFetch(
    `${props.baseURL}/api/v1/Search/LCBS/orgs/${organisationSelected?.id}/resources?search=${debouncedValue}`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    if (errorOrganisationData) {
      toasts.error(i18next.t('organisationListError'));
    }
    if (errorSiteList) {
      toasts.error(i18next.t('siteFetchError'));
    }
  }, [errorOrganisationData, errorSiteList]);

  const orgDataToSelectOptions = () => {
    return organisationData && organisationData.resources
      ? organisationData.resources.map(org => {
          return { value: org.id, text: org.name };
        })
      : [];
  };

  const selectOrgFromDropdown = (orgId: string) => {
    setValue('');
    return organisationData && organisationData.resources
      ? organisationData.resources.find(org => orgId === org.id)
      : undefined;
  };

  // Fetch organization lost
  useEffect(() => {
    fetchOrganisationData();
  }, []);

  // Fetch sites based on search API
  useEffect(() => {
    if (value !== '' && hasTargetSitePermission) {
      fetchSiteList();
    }
  }, [debouncedValue, organisationSelected, hasTargetSitePermission]);

  useEffect(() => {
    if (organisationSelected) {
      fetchPermissions();
    }
  }, [organisationSelected]);

  useEffect(() => {
    if (permissions) {
      hasEditSKUPermissions();
    }
  }, [permissions]);

  const hasEditSKUPermissions = () => {
    setHasTargetSitePermission(!!hasPermission(
      'SubscriptionManagement_SKU_Assign'
    ));
    if (permissions) {
      setHasAuthorizedError(!(!!hasPermission(
        'SubscriptionManagement_SKU_Assign'
      )));
    }
  };

  const onUnlinkClick = (cellData: ICellData) => {
    setSelectedSiteForUnlink(cellData.rowData);
    setShowUnlinkModal(true);
  };

  const onDeleteSiteClick = (cellData: ICellData) => {
    let tempList = [...associatedSites];
    tempList = tempList.filter(
      site => site.siteId !== cellData.rowData.siteId
    );
    setAssociatedSites(tempList);
    props.handleChange(
      tempList.filter(x => x.isNew === true),
      'associatedSites'
    );
  };

  const onActionClick = () => {
    return (
      <AlertModal
        onClose={() => setShowUnlinkModal(false)}
        message={i18next.t('unlinkMessage', {
          siteName: selectedSiteForUnlink && selectedSiteForUnlink.name,
          sku: props.skuName
        })}
      />
    );
  };

  const actionRenderer = (cellData: ICellData) => {
    if (cellData.rowData.isNew) {
      return (
        <Icon
          className="unlink-icon"
          root="building"
          name="delete"
          size="medium"
          onClick={() => onDeleteSiteClick(cellData)}
        />
      );
    } else {
      return (
        <UnLinkIcon className="unlink-icon" onClick={() => onUnlinkClick(cellData)}/>
      );
    }
  };

  const siteNameRenderer = (cellData: ICellData) => {
    if (cellData.rowData.isNew) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Badge color="red" empty />
          <div style={{ paddingLeft: 3 }}>{cellData.value} {(cellData.rowData.count > 1) && ('× ' + cellData.rowData.count)}</div>
        </div>
      );
    } else {
      return <div style={{ paddingLeft: 13 }}>{cellData.value} {(cellData.rowData.count > 1) && ('× ' + cellData.rowData.count)}</div>;
    }
  };

  const addressRenderer = (cellData: ICellData) => {
    if (!cellData.value) {
      return <>-</>;
    }
    return <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ paddingLeft: 3 }}>
        <Tooltip element={<Icon root='global' name='sites-pin' />} content={cellData.value} />
      </div>
    </div>
  };
  
  // handle site select from search list
  const handleSearchSelect = (result: any) => {
    setIsSiteSelected(true);
    setSiteSelected(result);
    setValue(result.name);
    if (associatedSites.find(x => x.id === result.id)) {
      setDuplicateSiteError(true);
    } else {
      setDuplicateSiteError(false);
    }
  };

  const onChangeNumberOfAddon = (value: any) => {
    if (value) {
      const numberOfAddon = Math.max(1, Math.min(props.totalQuantity - associatedSites.length, Number(value))).toString();
      setNumberOfAddon(numberOfAddon);
    } else {
      setNumberOfAddon('');
    }
  }

  // handle site search input change
  const handleSearchChange = (value: any) => {
    setIsSiteSelected(false);
    if (
      associatedSites.find(
        x => x.name.toLocaleLowerCase() === value.toLocaleLowerCase()
      )
    ) {
      setDuplicateSiteError(true);
    } else {
      setDuplicateSiteError(false);
    }
    setValue(value);
  };

  // handle clear inpt value from seach
  const handleClearSearch = () => {
    setIsSiteSelected(false);
    setDuplicateSiteError(false);
  };

  // get serched site list
  const getResultData = () => {
    let updatedSiteList: ISearchSiteList[] = [];
    if (!isLoadingSiteList &&
      siteList) {
        siteList.resources.forEach(site => {
          if (site.type === 'Site') {
            let updatedSite: ISearchSiteList = {
              title:  site.name,
              id: site.nodeId,
              name: site.name,
              desc: '',
              orgId: site.parentId,
              address: site.address
            };
            updatedSiteList.push(updatedSite);
          }
        });
    }
    return updatedSiteList;
  };

  const addSKU = () => {
    setDisplaySiteSection(false);
    let tempList = [...associatedSites];
    for(let i = 0; i < parseInt(numberOfAddon); i++) {
      tempList.push({
        orgId: organisationSelected?.id,
        siteId: siteSelected?.id,
        name: value,
        isNew: true,
        id: siteSelected ? siteSelected.id : '',
        address: siteSelected?.address || '',
        desc: ''
      });
    }
    setAssociatedSites(tempList);
    props.handleChange(
      tempList.filter(x => x.isNew === true),
      'associatedSites'
    );
    setValue('');
    setOrganisationSelected(undefined);
    props.onAddClick(true);
  };

  return (
    <div className="manage-sites">
      {showUnlinkModal && onActionClick()}
      {displaySitesection && (
        <div className="site-list-header-bar">
          <Select
            className="user-list-org-selector"
            options={orgDataToSelectOptions()}
            value={organisationSelected ? organisationSelected.id : ''}
            placeholder={
              !isLoadingOrganisationData ? i18next.t('selectOrg') : undefined
            }
            onChange={orgId => {
              setOrganisationSelected(selectOrgFromDropdown(orgId));
            }}
            loading={isLoadingOrganisationData || isLoadingPermissions}
          />
          { hasAuthorizedError && <div className="below-text"><span className="ui error-message">{i18next.t('notAuthorizedError')}</span></div>}
          {organisationSelected && hasTargetSitePermission && (
            <>
              <Search
                results={getResultData()}
                placeholder={i18next.t('searchSite')}
                value={value}
                className="search-site"
                onResultSelect={resultvalue => handleSearchSelect(resultvalue)}
                onSearchChange={searchValue => handleSearchChange(searchValue)}
                onResultClear={() => handleClearSearch()}
                noResultsMessage={i18next.t('noResultFound')}
                loading={isLoadingSiteList}
                error={duplicateSiteError && !props.isAddOn ? i18next.t('duplicateSiteError') : ''}
              />
              {props.isAddOn && <Input
                type="number"
                min={1}
                max={100}
                fluid={true}
                placeholder={i18next.t('numberOfAddon')}
                value={numberOfAddon}
                onChange={value => onChangeNumberOfAddon(value)}
                error={numberOfAddon === '' ? i18next.t('numberOfAddonRequired') : ''}
              />}
            </>
          )}
          <div className="site-header-buttons">
            <Button
              type="secondary"
              size="small"
              content={i18next.t('cancel')}
              onClick={() => setDisplaySiteSection(false)}
            />
            <Button
              type="primary"
              size="small"
              content={i18next.t('add')}
              onClick={() => addSKU()}
              disabled={value === '' || !isSiteSelected || numberOfAddon === '' || (duplicateSiteError && !props.isAddOn)}
            />
          </div>
        </div>
      )}

      <div className="site-list-detail">
        <div className="site-header">
          <div className="associated-site-count">
            {' '}
            {i18next.t('sitesAssociated', {
              siteCount: associatedSitesGroupBy.length
            })}
          </div>
          {!displaySitesection && (
            <div className="add-site">
              <Button
                type="secondary"
                size="small"
                content={i18next.t('addNewSite')}
                onClick={() => setDisplaySiteSection(true)}
                disabled={
                  props.totalQuantity === associatedSites.length
                }
              />
            </div>
          )}
        </div>

        <SplitPanelUtil>
          <SplitPanelUtil.Table>
            <DataTable
              data={associatedSitesGroupBy}
              emptyMessage={i18next.t('noRecordFound')}
            >
              <DataTable.Column
                field="name"
                header={i18next.t('siteName')}
                initialWidth={200}
                renderer={siteNameRenderer}
              />
              <DataTable.Column
                field="address"
                header={i18next.t('location')}
                initialWidth={200}
                renderer={addressRenderer}
              />
              <DataTable.Column
                field="action"
                header={i18next.t('action')}
                renderer={actionRenderer}
              />
            </DataTable>
          </SplitPanelUtil.Table>
        </SplitPanelUtil>
      </div>
    </div>
  );
};

export default consumeAPIs(ManageSites);
